class MycoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.previous = this.querySelector('[data-carousel-previous]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track || !this.previous || !this.next) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.previous.addEventListener('click', () => this.advance(-1), options);
    this.next.addEventListener('click', () => this.advance(1), options);
    this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
    this.track.addEventListener('focusin', event => this.reveal(event.target), options);
    this.track.addEventListener('change', event => this.reveal(event.target), options);
    this.track.addEventListener('dragstart', event => event.preventDefault(), options);
    this.track.addEventListener('pointerdown', event => {
      // Touch uses native scrolling so vertical page swipes remain available.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, moved: false };
      this.suppressClick = false;
    }, options);
    window.addEventListener('pointermove', event => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const distance = event.clientX - this.drag.x;
      if (Math.abs(distance) > 6 && !this.drag.moved) {
        this.drag.moved = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      }
      if (!this.drag.moved) return;
      event.preventDefault();
      this.track.scrollLeft = this.drag.left - distance;
    }, options);
    const endDrag = event => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.suppressClick = this.drag.moved;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.track.classList.remove('is-dragging');
      this.drag = null;
      this.updateControls();
    };
    window.addEventListener('pointerup', endDrag, options);
    window.addEventListener('pointercancel', endDrag, options);
    this.track.addEventListener('click', event => {
      if (!this.suppressClick || event.detail === 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.suppressClick = false;
    }, { ...options, capture: true });
    this.observer = new ResizeObserver(() => this.updateControls());
    this.observer.observe(this.track);
    for (const item of this.track.children) this.observer.observe(item);
    this.updateControls();
    this.reveal(this.track.querySelector('input:checked, .is-active'), 'auto');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.drag = null;
  }

  updateControls() {
    const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
    this.toggleAttribute('data-overflow', overflow);
    this.previous.hidden = !overflow;
    this.next.hidden = !overflow;
    this.previous.disabled = this.track.scrollLeft <= 2;
    this.next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
  }

  advance(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth * .8, behavior: this.motion() });
  }

  motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  reveal(target, behavior = this.motion()) {
    const item = target?.closest('.product-page__thumbnail, .product-form__variant');
    if (!item || !this.track.contains(item)) return;
    const viewport = this.track.getBoundingClientRect();
    const bounds = item.getBoundingClientRect();
    const distance = bounds.left < viewport.left + 3 ? bounds.left - viewport.left - 3
      : bounds.right > viewport.right - 3 ? bounds.right - viewport.right + 3 : 0;
    if (distance) this.track.scrollBy({ left: distance, behavior });
  }
}
if (!customElements.get('myco-carousel')) customElements.define('myco-carousel', MycoCarousel);

(() => {
  const menuButton = document.querySelector('[data-menu-toggle]');
  const menu = document.getElementById('mobile-menu');
  const searchButton = document.querySelector('[data-search-toggle]');
  const search = document.getElementById('search-panel');
  const setOpen = (button, panel, open) => {
    if (!button || !panel) return;
    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
  };
  menuButton?.addEventListener('click', () => {
    const next = menuButton.getAttribute('aria-expanded') !== 'true';
    setOpen(searchButton, search, false);
    setOpen(menuButton, menu, next);
  });
  searchButton?.addEventListener('click', () => {
    const next = searchButton.getAttribute('aria-expanded') !== 'true';
    setOpen(menuButton, menu, false);
    setOpen(searchButton, search, next);
    if (next) search?.querySelector('input')?.focus();
  });
  menu?.addEventListener('click', event => {
    if (event.target.closest('a')) setOpen(menuButton, menu, false);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      setOpen(menuButton, menu, false);
      setOpen(searchButton, search, false);
    }
  });
  const productPage = document.querySelector('[data-product-page]');
  const mainImage = productPage?.querySelector('[data-product-main-image]');
  const galleryButtons = productPage?.querySelectorAll('[data-gallery-image]') || [];
  const showImage = (source, alt) => {
    if (!mainImage || !source) return;
    // Remove the original responsive candidates when changing the selected media.
    mainImage.removeAttribute('srcset');
    mainImage.src = source;
    mainImage.alt = alt || '';
    galleryButtons.forEach(button => {
      const active = button.dataset.galleryImage === source;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      if (active) button.closest('myco-carousel')?.reveal(button);
    });
  };
  galleryButtons.forEach(button => button.addEventListener('click', () => showImage(button.dataset.galleryImage, button.dataset.galleryAlt)));
  productPage?.querySelectorAll('[data-variant-option]').forEach(input => {
    input.addEventListener('change', () => {
      if (!input.checked) return;
      productPage.querySelectorAll('.product-form__variant').forEach(label => label.classList.toggle('is-selected', label.contains(input)));
      const label = productPage.querySelector('[data-variant-name]');
      const price = productPage.querySelector('[data-product-price]');
      const buttonPrice = productPage.querySelector('[data-product-button-price]');
      if (label) label.textContent = input.closest('label')?.querySelector('span')?.textContent || '';
      if (price) price.textContent = input.dataset.price || '';
      if (buttonPrice) buttonPrice.textContent = input.dataset.price || '';
      showImage(input.dataset.image, input.dataset.imageAlt);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState({}, '', url);
    });
  });
  document.querySelectorAll('[data-quantity-change]').forEach(button => button.addEventListener('click', () => {
    const input = button.parentElement?.querySelector('input[type="number"]');
    if (!input) return;
    const minimum = Number(input.min || 0);
    input.value = String(Math.max(minimum, Number(input.value || minimum) + Number(button.dataset.quantityChange)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  const cartForm = document.querySelector('[data-cart-form]');
  cartForm?.addEventListener('submit', () => {
    const count = [...cartForm.querySelectorAll('input[name="updates[]"]')].reduce((total, input) => total + Math.max(0, Math.floor(Number(input.value) || 0)), 0);
    const note = cartForm.querySelector('[data-gift-note]');
    const gift = cartForm.querySelector('input[name="attributes[Complimentary gift]"]');
    if (!note || !gift) return;
    const message = note.dataset.giftMessage;
    const customerNote = note.value.replace(message, '').trim();
    gift.value = count >= 5 ? message : '';
    note.value = [customerNote, count >= 5 ? message : ''].filter(Boolean).join('\n');
  });
  let updateTimer;
  cartForm?.querySelectorAll('input[name="updates[]"]').forEach(input => input.addEventListener('change', () => {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(() => cartForm.requestSubmit(cartForm.querySelector('[data-cart-update]')), 350);
  }));
  document.querySelector('[data-sort-select]')?.addEventListener('change', event => event.target.form?.requestSubmit());
})();
